import { useState, useCallback, useEffect, useRef } from 'react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Alert } from '../components/ui/Alert';
import { ProgressBar } from '../components/ui/ProgressBar';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { listHospitals } from '../services/hospitalService';
import {
  startBulkRecalculation,
  getBulkRecalculationStatus,
  type BulkRecalculationJobStatus,
} from '../services/calculationService';
import type { Hospital } from '../types';

export default function BulkRecalculationPage() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);
  const [hospitalUuid, setHospitalUuid] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');

  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState<BulkRecalculationJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load hospitals on mount
  useEffect(() => {
    listHospitals({ limit: 1000, active: 'true' })
      .then((res) => setHospitals(res.hospitals))
      .catch(() => {})
      .finally(() => setIsLoadingHospitals(false));
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback(
    (id: string) => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const status = await getBulkRecalculationStatus(id);
          setJobStatus(status);
          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch {
          // keep polling on transient errors
        }
      }, 2000);
    },
    []
  );

  const handleStart = useCallback(async () => {
    if (!hospitalUuid || !dateFrom || !dateTill) return;
    setIsStarting(true);
    setStartError('');
    setJobId('');
    setJobStatus(null);

    try {
      const res = await startBulkRecalculation(hospitalUuid, dateFrom, dateTill);
      setJobId(res.jobId);
      startPolling(res.jobId);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.error ||
        (Array.isArray(data?.errors) ? data.errors.join('; ') : null) ||
        err?.message ||
        'Failed to start bulk recalculation.';
      setStartError(msg);
    } finally {
      setIsStarting(false);
    }
  }, [hospitalUuid, dateFrom, dateTill, startPolling]);

  const isRunning = jobStatus?.status === 'PENDING' || jobStatus?.status === 'RUNNING';
  const isComplete = jobStatus?.status === 'COMPLETED';
  const isFailed = jobStatus?.status === 'FAILED';
  const progress =
    jobStatus && jobStatus.progress.total > 0
      ? (jobStatus.progress.processed / jobStatus.progress.total) * 100
      : 0;

  if (isLoadingHospitals) {
    return (
      <div className="bulk-recalc-page">
        <LoadingSpinner message="Loading hospitals..." />
      </div>
    );
  }

  return (
    <div className="bulk-recalc-page">
      <div className="page-header">
        <h2>Bulk Recalculation</h2>
      </div>

      <section className="recalc-section">
        <div className="bulk-recalc-form">
          <Select
            label="Hospital"
            options={hospitals.map((h) => ({
              value: h.hospitalUuid,
              label: `${h.hospitalName} (${h.hospitalCode})`,
            }))}
            placeholder="Select hospital..."
            value={hospitalUuid}
            onChange={(e) => setHospitalUuid(e.target.value)}
            disabled={isRunning}
          />
          <Input
            label="Date From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            disabled={isRunning}
          />
          <Input
            label="Date Till"
            type="date"
            value={dateTill}
            onChange={(e) => setDateTill(e.target.value)}
            disabled={isRunning}
          />
        </div>
        <div className="recalc-actions">
          <Button
            size="lg"
            onClick={handleStart}
            isLoading={isStarting}
            disabled={!hospitalUuid || !dateFrom || !dateTill || isStarting || isRunning}
          >
            Start Bulk Recalculation
          </Button>
        </div>
        {startError && <Alert type="error">{startError}</Alert>}
      </section>

      {/* Job Progress */}
      {jobId && (
        <section className="recalc-section" aria-label="Job progress">
          <h3>Job: {jobId}</h3>

          {isRunning && (
            <>
              <ProgressBar
                value={progress}
                label={`Processing ${jobStatus?.progress.processed ?? 0} / ${jobStatus?.progress.total ?? '?'}`}
              />
              <LoadingSpinner size="sm" message="Recalculating appointments..." />
            </>
          )}

          {isComplete && jobStatus && (
            <div className="bulk-recalc-summary">
              <Alert type="success">Bulk recalculation completed.</Alert>
              <div className="bulk-recalc-stats">
                <div className="bulk-stat">
                  <span className="bulk-stat-label">Total Processed</span>
                  <span className="bulk-stat-value">{jobStatus.progress.processed}</span>
                </div>
                <div className="bulk-stat">
                  <span className="bulk-stat-label">Changed</span>
                  <span className="bulk-stat-value">{jobStatus.progress.changed}</span>
                </div>
                <div className="bulk-stat">
                  <span className="bulk-stat-label">Errors</span>
                  <span className="bulk-stat-value">{jobStatus.progress.errors}</span>
                </div>
              </div>
            </div>
          )}

          {isFailed && (
            <Alert type="error">
              Job failed: {jobStatus?.error || 'Unknown error'}
            </Alert>
          )}
        </section>
      )}
    </div>
  );
}
